import moment from "moment-timezone";
import { TimezoneOption } from "@core/features/timezones";
import { Timeslot } from "../timeslots.types";
import calculateTimeslotId from "./calculateTimeslotId";
import createTimeslotId from "./createTimeslotId";
import { packTimeslotDateData } from "./packTimeslotDateData";

export type Args = {
  /** Grid start hour in local time (e.g., 6 for 6 AM) */
  startHour: number;
  /** Grid end hour in local time (e.g., 23 for 11 PM) */
  endHour: number;
  /** User's selected timezone */
  timezone: TimezoneOption;
  /** Array of local date strings in ISO format (e.g., ['2025-01-15', '2025-01-16']) */
  localDates: string[];
};

/**
 * Generate timeslots for a range of hours across specified local dates.
 * Each date gets slots from startHour to endHour in the user's local timezone.
 * 
 * The slot ID is a weekly ID (0-335) based on UTC time, used for backend storage.
 * The local/utc properties contain the actual datetime for display.
 */
export function makeTimeslotsForRange({
  startHour,
  endHour,
  timezone,
  localDates,
}: Args): Timeslot[] {
  if (!timezone?.name) throw new Error("Timezone is required");
  if (!(startHour >= 0 && endHour <= 24 && startHour < endHour)) {
    throw new Error("Invalid hour range");
  }
  if (!localDates || localDates.length === 0) {
    throw new Error("localDates array is required and must not be empty");
  }

  const slots: Timeslot[] = [];

  // Iterate over each provided local date
  for (const localDate of localDates) {
    for (let localHour = startHour; localHour < endHour; localHour++) {
      for (const localMinute of [0, 30]) {
        // Create moment in the user's local timezone
        const localStart = moment.tz(localDate, timezone.name)
          .hour(localHour)
          .minute(localMinute)
          .second(0)
          .millisecond(0);

        const localEnd = localStart.clone().add(30, "minutes");

        // Convert to UTC
        const utcStart = localStart.clone().utc();
        const utcEnd = localEnd.clone().utc();

        // Calculate weekly slot ID based on UTC time
        const utcDayIndex = utcStart.day(); // 0 = Sunday
        const utcHour = utcStart.hour();
        const utcMinute = utcStart.minute();

        const id = createTimeslotId(
          calculateTimeslotId({
            dayIndex: utcDayIndex,
            hour: utcHour,
            minute: utcMinute,
          })
        );

        slots.push({
          id,
          local: {
            start: packTimeslotDateData(localStart),
            end: packTimeslotDateData(localEnd),
          },
          utc: {
            start: packTimeslotDateData(utcStart),
            end: packTimeslotDateData(utcEnd),
          },
        });
      }
    }
  }

  return slots;
}

export default makeTimeslotsForRange;

